import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  List,
  Modal,
  Row,
  Segmented,
  Space,
  Statistic,
  Tag,
  Typography
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { getAlerts, getOverview, readAlerts } from "./api";
import { LIGHT_COLORS, formatDay, qualificationLabels, stateLabels } from "./format";

const NEEDS_ACTION = ["missing", "overdue", "due_soon"];

const StateTag = ({ row, labels }) => (
  <Tag color={LIGHT_COLORS[row.light]} className="sf-state">
    {labels[row.state]}
  </Tag>
);

/** Who needs which Unterweisung when, expiring qualifications and review dates, worst first. */
const DueOverview = ({ onGoTo }) => {
  const { t } = useTranslation("dashboard");
  const [asOf, setAsOf] = useState(null);
  const [filter, setFilter] = useState("action");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState(null);
  const labels = stateLabels(t);
  const types = qualificationLabels(t);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getOverview(asOf ? asOf.format("YYYY-MM-DD") : undefined);
      setData(res.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [asOf]);

  const openAlerts = async () => {
    try {
      const res = await getAlerts();
      setAlerts(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const markAllRead = async () => {
    try {
      await readAlerts();
      setAlerts(null);
      load();
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const show = rows => (filter === "all" ? rows : rows.filter(r => NEEDS_ACTION.includes(r.state)));
  const due = show(data?.due || []);
  const qualifications = show((data?.qualifications || []).filter(q => q.state !== "none"));
  const reviews = show(data?.reviews || []);
  const counts = data?.dueCounts || {};
  const qCounts = data?.qualificationCounts || {};

  if (data && !data.topics) {
    return (
      <Card>
        <Empty
          description={
            <>
              <Typography.Title level={5}>
                {t("Start with the instruction topics")}
              </Typography.Title>
              <Typography.Paragraph type="secondary" className="sf-empty-text">
                {t(
                  "Employees must be instructed when they start and at least once a year (§ 12 ArbSchG, DGUV Vorschrift 1). Choose the topics that apply to your company; the due list then shows who needs what."
                )}
              </Typography.Paragraph>
            </>
          }
        >
          <Button type="primary" onClick={() => onGoTo("topics")}>
            {t("Choose topics")}
          </Button>
        </Empty>
      </Card>
    );
  }

  return (
    <>
      {data?.unreadAlerts > 0 && (
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message={t("{{n}} new reminders", { n: data.unreadAlerts })}
          action={
            <Button size="small" onClick={openAlerts}>
              {t("Show")}
            </Button>
          }
        />
      )}

      <Row gutter={[12, 12]} className="mb-3">
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title={t("Instructions missing or overdue")}
              value={(counts.missing || 0) + (counts.overdue || 0)}
              valueStyle={{ color: "var(--ds-danger, #c93c30)" }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title={t("Instructions due soon")} value={counts.due_soon || 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title={t("Certificates expired")}
              value={qCounts.overdue || 0}
              valueStyle={{ color: "var(--ds-danger, #c93c30)" }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title={t("Certificates expiring soon")} value={qCounts.due_soon || 0} />
          </Card>
        </Col>
      </Row>

      <Space wrap className="mb-3">
        <Segmented
          value={filter}
          onChange={setFilter}
          options={[
            { value: "action", label: t("Needs action") },
            { value: "all", label: t("All") }
          ]}
        />
        <DatePicker
          value={asOf}
          onChange={setAsOf}
          format="DD.MM.YYYY"
          placeholder={t("Look ahead to")}
          disabledDate={day => day.isBefore(dayjs(), "day")}
        />
      </Space>

      <Card title={t("Safety instructions per employee")} className="mb-3" size="small">
        <ResponsiveTable
          rowKey={row => `${row.employee}:${row.topic}`}
          loading={loading}
          dataSource={due}
          mobileAvatar={false}
          locale={{ emptyText: t("Nothing due") }}
          pagination={due.length > 50 ? { pageSize: 50 } : false}
          columns={[
            { title: t("Employee"), dataIndex: "employeeName", key: "employee", mobile: "title" },
            { title: t("Topic"), dataIndex: "topicTitle", key: "topic", mobile: "subtitle" },
            {
              title: t("Status"),
              key: "state",
              mobile: "badge",
              render: (_, row) => <StateTag row={row} labels={labels} />
            },
            {
              title: t("Last instruction"),
              key: "last",
              render: (_, row) => formatDay(row.lastDate) || "—"
            },
            { title: t("Due on"), key: "due", render: (_, row) => formatDay(row.dueDate) }
          ]}
        />
      </Card>

      <Card title={t("Qualifications & checks")} className="mb-3" size="small">
        <ResponsiveTable
          rowKey="_id"
          loading={loading}
          dataSource={qualifications}
          mobileAvatar={false}
          locale={{ emptyText: t("Nothing due") }}
          pagination={qualifications.length > 50 ? { pageSize: 50 } : false}
          columns={[
            { title: t("Employee"), dataIndex: "employeeName", key: "employee", mobile: "title" },
            {
              title: t("Qualification"),
              key: "type",
              mobile: "subtitle",
              render: (_, row) => [types[row.type], row.title].filter(Boolean).join(": ")
            },
            {
              title: t("Status"),
              key: "state",
              mobile: "badge",
              render: (_, row) => <StateTag row={row} labels={labels} />
            },
            { title: t("Valid until"), key: "valid", render: (_, row) => formatDay(row.validUntil) }
          ]}
        />
      </Card>

      {reviews.length > 0 && (
        <Card title={t("Risk assessment reviews")} size="small">
          <ResponsiveTable
            rowKey="_id"
            dataSource={reviews}
            mobileAvatar={false}
            pagination={false}
            columns={[
              {
                title: t("Risk assessment"),
                key: "title",
                mobile: "title",
                render: (_, row) => (
                  <Link to={`/user/safety/risk-assessments/${row._id}`}>
                    {row.number} {row.title}
                  </Link>
                )
              },
              {
                title: t("Status"),
                key: "state",
                mobile: "badge",
                render: (_, row) => <StateTag row={row} labels={labels} />
              },
              {
                title: t("Review by"),
                key: "review",
                render: (_, row) => formatDay(row.reviewDate)
              }
            ]}
          />
        </Card>
      )}

      <Modal
        rootClassName="safety-page"
        open={Boolean(alerts)}
        title={t("Reminders")}
        onCancel={() => setAlerts(null)}
        footer={[
          <Button key="close" onClick={() => setAlerts(null)}>
            {t("Close")}
          </Button>,
          <Button key="read" type="primary" onClick={markAllRead}>
            {t("Mark all as read")}
          </Button>
        ]}
      >
        <List
          dataSource={alerts || []}
          renderItem={alert => (
            <List.Item>
              <List.Item.Meta
                title={
                  <Space wrap>
                    {!alert.readAt && <Tag color="processing">{t("New")}</Tag>}
                    <span className="sf-wrap">{alert.title}</span>
                  </Space>
                }
                description={
                  alert.step === "due"
                    ? t("Due since {{date}}", { date: formatDay(alert.dueDate) })
                    : t("Due on {{date}}", { date: formatDay(alert.dueDate) })
                }
              />
            </List.Item>
          )}
        />
      </Modal>
    </>
  );
};

export default DueOverview;
